/**
 * Energy Certificate Simulator - EP Calculation Engine
 *
 * Pure functions for calculating the EP indicator (Primary Energy)
 * based on Polish building regulations:
 * - Dz.U. 2023 poz. 697 (methodology)
 * - PN-EN ISO 52016-1:2017-09 (calculation standards)
 * - WT 2021 (building thermal requirements)
 *
 * @license Proprietary - Commercial module
 */

import type {
  SimulatorInput,
  SimulatorResult,
  BuildingGeometry,
  UValues,
  Recommendation,
} from '../types';
import { validateSimulatorInput } from './validation';
import {
  getPrimaryEnergyCoefficient,
  getSystemEfficiency,
  isHeatPump,
  getHotWaterEfficiency,
  getHotWaterPrimaryCoefficient,
  POLAND_CLIMATE,
  CLIMATE_ZONES,
  AIR_CHANGE_RATES,
  RECUPERATION_EFFICIENCY,
  PV_PARAMETERS,
  SOLAR_THERMAL_PARAMETERS,
} from '../constants/coefficients';
import {
  getWallUValue,
  getRoofUValue,
  getWindowUValue,
  getFloorUValue,
  getThermalBridgeCoefficient,
} from '../constants/uValueDefaults';
import {
  determineEnergyClass,
  getEPLimit,
} from '../constants/energyClassThresholds';

// =============================================================================
// CONSTANTS
// =============================================================================

/** Ceiling height [m] */
const CEILING_HEIGHT = 2.7;

/** Window-to-wall ratio */
const WINDOW_TO_WALL_RATIO = 0.2;

/** Internal heat gains [W/m²] */
const INTERNAL_HEAT_GAINS = 3.5;


/**
 * Monthly solar irradiance on south-facing vertical surface [kWh/m²·month]
 * Source: PN-EN ISO 52010 / IMGW typical meteorological year for Warsaw (zone III)
 * Index 0 = January, 11 = December
 */
const MONTHLY_SOLAR_IRRADIANCE_SOUTH: number[] = [
  25,  // Jan - low sun angle, short days
  38,  // Feb - improving
  58,  // Mar
  68,  // Apr - spring, good angle for vertical
  72,  // May
  70,  // Jun - high sun, less on vertical
  68,  // Jul
  72,  // Aug
  62,  // Sep
  45,  // Oct
  25,  // Nov
  18,  // Dec - lowest
];

/**
 * Monthly average outdoor temperatures for zone III [°C]
 * Source: IMGW typical meteorological year, Warsaw
 */
const MONTHLY_OUTDOOR_TEMPS: number[] = [
  -1.5, // Jan
  -0.5, // Feb
   3.5, // Mar
   9.0, // Apr
  14.5, // May
  17.5, // Jun
  19.5, // Jul
  19.0, // Aug
  14.0, // Sep
   8.5, // Oct
   3.0, // Nov
  -0.5, // Dec
];

/** Solar gain factors: g-value × frame factor × shading */
const SOLAR_GAIN_COEFFICIENT = 0.5 * 0.7 * 0.9; // = 0.315

/**
 * Orientation factor for solar gains
 * Multiplier applied to solar irradiance based on window orientation.
 * South-facing windows receive ~2.5x more solar energy than north-facing
 * during the heating season in Poland (latitude ~52°N).
 */
const ORIENTATION_SOLAR_FACTORS: Record<string, number> = {
  south: 1.0,       // Reference: maximum solar gains
  southeast: 0.85,  // Good morning + partial afternoon
  southwest: 0.85,  // Partial morning + good afternoon
  east: 0.65,       // Morning sun only
  west: 0.65,       // Afternoon sun only
  northeast: 0.45,  // Limited morning
  northwest: 0.45,  // Limited afternoon
  north: 0.4,       // Minimal direct solar gains (diffuse only)
};

/** Hot water demand per person per day [l/person/day] */
const HOT_WATER_DEMAND_PER_PERSON = 40;

/** Assumed occupancy per 30 m² of floor area */
const OCCUPANCY_PER_30M2 = 1;

// =============================================================================
// BUILDING GEOMETRY
// =============================================================================

/**
 * Calculate building geometry from input parameters
 */
export function calculateBuildingGeometry(input: SimulatorInput): BuildingGeometry {
  const { usableArea, floors, buildingType } = input;

  // Floor area per level
  const floorAreaPerLevel = usableArea / floors;

  // Building footprint (simplified: square building)
  const footprintSide = Math.sqrt(floorAreaPerLevel);

  // Total height
  const totalHeight = floors * CEILING_HEIGHT;

  // Volume
  const volume = usableArea * CEILING_HEIGHT;

  // Perimeter (adjusted for building type)
  let exposedWallsCount = 4; // Default for detached

  switch (buildingType) {
    case 'semi_detached':
      exposedWallsCount = 3; // One wall shared
      break;
    case 'terraced':
      exposedWallsCount = 2; // Two walls shared
      break;
    case 'apartment':
      exposedWallsCount = 1.5; // Average for apartment
      break;
    default:
      exposedWallsCount = 4;
  }

  // Wall area calculation
  const wallPerimeter = footprintSide * exposedWallsCount;
  const totalWallArea = wallPerimeter * totalHeight;

  // Window area (percentage of wall area)
  const windowArea = totalWallArea * WINDOW_TO_WALL_RATIO;

  // Net wall area (excluding windows)
  const netWallArea = totalWallArea - windowArea;

  // Roof area (only top floor)
  let roofArea = floorAreaPerLevel;
  if (buildingType === 'apartment') {
    // Assume middle-floor apartment has no roof exposure, but we'll use roof for top floor
    roofArea = floorAreaPerLevel * 0.5; // Average
  }

  // Ground floor area (only for buildings on ground)
  const groundFloorArea = buildingType === 'apartment' ? 0 : floorAreaPerLevel;

  // Shape coefficient (A/V)
  const totalEnvelopeArea = netWallArea + windowArea + roofArea + groundFloorArea;
  const shapeCoefficient = totalEnvelopeArea / volume;

  return {
    floorArea: usableArea,
    volume,
    wallArea: netWallArea,
    roofArea,
    groundFloorArea,
    windowArea,
    shapeCoefficient,
  };
}

// =============================================================================
// U-VALUES
// =============================================================================

/**
 * Map renovation year to equivalent building period for U-value lookup.
 * When a building is renovated, the thermal envelope is upgraded to
 * the standards of the renovation era.
 */
function renovationYearToPeriod(year: number): import('../types').BuildingPeriod {
  if (year >= 2021) return '2021_plus';
  if (year >= 2014) return '2014_2020';
  if (year >= 2009) return '2009_2013';
  if (year >= 1999) return '1999_2008';
  if (year >= 1986) return '1986_1998';
  return '1967_1985';
}

/**
 * Get U-values for building envelope, accounting for thermal renovation.
 *
 * - 'none': use building period U-values as-is
 * - 'partial': only walls improved to renovation-era standards
 * - 'full': walls, roof, floor, and thermal bridges all improved
 */
export function getUValues(input: SimulatorInput): UValues {
  const { buildingPeriod, wallInsulation, roofInsulation, windowType } = input;

  let wallU = getWallUValue(buildingPeriod, wallInsulation);
  let roofU = getRoofUValue(buildingPeriod, roofInsulation);
  let floorU = getFloorUValue(buildingPeriod);
  let bridgeU = getThermalBridgeCoefficient(buildingPeriod);

  if (input.renovationStatus !== 'none' && input.renovationYear) {
    const renoPeriod = renovationYearToPeriod(input.renovationYear);

    if (input.renovationStatus === 'full') {
      wallU = Math.min(wallU, getWallUValue(renoPeriod, wallInsulation));
      roofU = Math.min(roofU, getRoofUValue(renoPeriod, roofInsulation));
      floorU = Math.min(floorU, getFloorUValue(renoPeriod));
      bridgeU = Math.min(bridgeU, getThermalBridgeCoefficient(renoPeriod));
    } else {
      // Partial: only walls improved
      wallU = Math.min(wallU, getWallUValue(renoPeriod, wallInsulation));
    }
  }

  return {
    walls: wallU,
    roof: roofU,
    windows: getWindowUValue(windowType),
    floor: floorU,
    thermalBridges: bridgeU,
  };
}

// =============================================================================
// HEAT LOSS COEFFICIENT
// =============================================================================

interface HeatLossCoefficient {
  transmission: number;
  ventilation: number;
  total: number;
}

/**
 * Calculate heat loss coefficient H [W/K]
 *
 * H = H_tr (transmission) + H_ve (ventilation)
 */
export function calculateHeatLossCoefficient(
  geometry: BuildingGeometry,
  uValues: UValues
): HeatLossCoefficient {
  // Transmission losses: H_tr = Σ(A × U)
  const H_walls = geometry.wallArea * uValues.walls;
  const H_roof = geometry.roofArea * uValues.roof;
  const H_windows = geometry.windowArea * uValues.windows;
  const H_floor = geometry.groundFloorArea * uValues.floor * 0.5; // Ground has ~50% heat flow

  // Thermal bridges: approximate as additional percentage
  const thermalBridgeLength = 2 * Math.sqrt(geometry.floorArea) * 4; // Perimeter × floors estimate
  const H_bridges = thermalBridgeLength * uValues.thermalBridges;

  const transmission = H_walls + H_roof + H_windows + H_floor + H_bridges;

  // Ventilation losses: H_ve = 0.34 × n × V
  // 0.34 = ρ_air × c_air / 3600 [Wh/(m³·K)]
  const airChangeRate = AIR_CHANGE_RATES.natural;
  const ventilation = 0.34 * airChangeRate * geometry.volume;

  return {
    transmission,
    ventilation,
    total: transmission + ventilation,
  };
}

// =============================================================================
// USEFUL ENERGY
// =============================================================================

/**
 * Calculate useful energy demand for heating [kWh/year]
 *
 * Q_H,nd = (H_tr + H_ve) × (ti - te) × t_heating - Q_gains
 */
/**
 * Resolve climate data from zone. Falls back to POLAND_CLIMATE (zone III) if not specified.
 */
function getClimateData(climateZone?: import('../types').ClimateZone) {
  if (climateZone && climateZone in CLIMATE_ZONES) {
    return CLIMATE_ZONES[climateZone];
  }
  return POLAND_CLIMATE;
}

export function calculateUsefulEnergy(
  heatLossCoefficient: HeatLossCoefficient,
  volume: number,
  windowArea?: number,
  orientationFactor?: number,
  climateZone?: import('../types').ClimateZone
): number {
  const climate = getClimateData(climateZone);
  const { indoorTemp } = climate;
  const floorArea = volume / CEILING_HEIGHT;
  const effectiveWindowArea = windowArea ?? (floorArea * WINDOW_TO_WALL_RATIO);
  const orientMult = orientationFactor ?? 0.7;

  // Monthly calculation method (PN-EN ISO 13790 simplified monthly)
  let totalUsefulEnergy = 0;
  const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Offset monthly temps from zone III baseline to actual climate zone
  const zoneIIIAvg = 4.5; // MONTHLY_OUTDOOR_TEMPS are for zone III
  const tempOffset = climate.avgOutdoorTemp - zoneIIIAvg;

  for (let month = 0; month < 12; month++) {
    const tOut = MONTHLY_OUTDOOR_TEMPS[month] + tempOffset;

    // Only count heating months (outdoor temp < indoor temp - 3°C threshold)
    if (tOut >= indoorTemp - 3) continue;

    const deltaT = indoorTemp - tOut;
    const hoursInMonth = DAYS_PER_MONTH[month] * 24;

    // Heat losses this month [kWh]
    const monthlyHeatLoss = (heatLossCoefficient.total * deltaT * hoursInMonth) / 1000;

    // Internal gains this month [kWh]
    const monthlyInternalGains = (INTERNAL_HEAT_GAINS * floorArea * hoursInMonth) / 1000;

    // Solar gains this month [kWh]
    // Use monthly irradiance data × orientation × glass+frame coefficient
    const monthlyIrradiance = MONTHLY_SOLAR_IRRADIANCE_SOUTH[month]; // kWh/m² for south
    const monthlySolarGains = monthlyIrradiance * effectiveWindowArea * orientMult * SOLAR_GAIN_COEFFICIENT;

    // Monthly utilization factor (higher when loss/gain ratio is larger)
    const totalGains = monthlyInternalGains + monthlySolarGains;
    const gamma = totalGains > 0 ? monthlyHeatLoss / totalGains : 10;
    const a = 1 + (climate.heatingDegreeDays > 3800 ? 15 : 12) / 3600; // time constant proxy
    const utilizationFactor = gamma > 0 ? (1 - Math.pow(gamma, -a)) / (1 - Math.pow(gamma, -(a + 1))) : 1;

    // Monthly useful energy
    const monthlyUseful = Math.max(0, monthlyHeatLoss - totalGains * Math.min(utilizationFactor, 0.99));
    totalUsefulEnergy += monthlyUseful;
  }

  return totalUsefulEnergy;
}

// =============================================================================
// HOT WATER EP
// =============================================================================

/**
 * Calculate EP for domestic hot water [kWh/m²·year]
 */
export function calculateHotWaterEP(input: SimulatorInput): { ep: number; usefulEnergy: number } {
  const { usableArea, hotWaterSource, heatingSystem } = input;
  const climate = getClimateData(input.climateZone);

  // Use user-provided occupants, fall back to estimate if not set
  const occupants = input.occupants > 0
    ? input.occupants
    : Math.max(2, Math.ceil(usableArea / 30) * OCCUPANCY_PER_30M2);

  // Allow user overrides for hot water parameters (advanced mode)
  const dailyPerPerson = input.dailyHotWaterPerPerson ?? HOT_WATER_DEMAND_PER_PERSON;
  const tHot = input.hotWaterTemp ?? climate.hotWaterTemp;
  const tCold = input.coldWaterTemp ?? climate.coldWaterTemp;

  // Annual hot water demand [kWh/year]
  // Q_W = 4.19 × V_w × (t_w - t_cw) × 365 / 3600
  const dailyVolume = dailyPerPerson * occupants; // liters
  const tempDiff = tHot - tCold;
  const annualDemand = (4.19 * dailyVolume * tempDiff * 365) / 3600;

  // For solar hot water, renewable energy covers ~60% of demand (free energy)
  const solarCoverage = hotWaterSource === 'solar' ? 0.6 : 0;
  const nonRenewableDemand = annualDemand * (1 - solarCoverage);

  // Hot water system efficiency (for backup system when solar is used)
  const efficiency = hotWaterSource === 'solar' ? 0.95 : getHotWaterEfficiency(hotWaterSource);

  // Final energy (only for non-renewable portion)
  const finalEnergy = nonRenewableDemand / efficiency;

  // Primary energy coefficient
  let wi: number;
  if (hotWaterSource === 'combined') {
    // Use heating system coefficient
    wi = getPrimaryEnergyCoefficient(heatingSystem);
    if (isHeatPump(heatingSystem)) {
      // For heat pumps, divide by COP
      const cop = getSystemEfficiency(heatingSystem, 'new');
      wi = wi / cop;
    }
  } else if (hotWaterSource === 'solar') {
    // Solar backup is typically electric, but only for non-renewable portion
    wi = 2.5;
  } else {
    wi = getHotWaterPrimaryCoefficient(hotWaterSource);
  }

  // Primary energy
  const primaryEnergy = finalEnergy * wi;

  // EP [kWh/m²·year]
  return {
    ep: primaryEnergy / usableArea,
    usefulEnergy: annualDemand,
  };
}

// =============================================================================
// OZE REDUCTION
// =============================================================================

/**
 * Calculate EP reduction from renewable energy sources [kWh/m²·year]
 */
/** Maximum total OZE reduction as fraction of epBeforeOZE */
const MAX_TOTAL_OZE_REDUCTION = 0.70;

export function calculateOZEReduction(
  input: SimulatorInput,
  epTotalBeforeOZE: number
): number {
  const { hasPV, pvPower, renewables, usableArea } = input;
  let reduction = 0;

  // Photovoltaic reduction
  if (hasPV && pvPower && pvPower > 0) {
    // Annual production [kWh/year]
    const pvProduction = pvPower * PV_PARAMETERS.annualProductionPerKwp;

    // Primary energy equivalent
    // EP_PV = P_PV × wi × self_consumption / A_f
    const pvPrimaryEnergy = pvProduction * 2.5 * PV_PARAMETERS.selfConsumptionFactor;
    const pvReduction = pvPrimaryEnergy / usableArea;

    // Cap PV at 70% of total EP
    const maxReduction = epTotalBeforeOZE * PV_PARAMETERS.maxEPReductionFactor;
    reduction += Math.min(pvReduction, maxReduction);
  }

  // NOTE: Recuperation is NOT counted here as OZE reduction.
  // It is already applied in calculateEP() step 4 by reducing ventilation heat loss.
  // Counting it here would double-count the benefit.

  // Solar thermal (collectors)
  if (renewables.includes('solar_thermal')) {
    // Reduces hot water demand
    reduction += epTotalBeforeOZE * 0.05 * SOLAR_THERMAL_PARAMETERS.efficiency;
  }

  // Ground heat exchanger
  if (renewables.includes('ground_exchanger')) {
    // Preheats ventilation air
    reduction += epTotalBeforeOZE * 0.03;
  }

  // Cap total OZE reduction at 70% of epBeforeOZE
  const maxTotalReduction = epTotalBeforeOZE * MAX_TOTAL_OZE_REDUCTION;
  return Math.min(reduction, maxTotalReduction);
}

// =============================================================================
// RECOMMENDATIONS
// =============================================================================

/**
 * Generate modernization recommendations based on building characteristics
 */
export function generateRecommendations(
  input: SimulatorInput,
  epTotal: number
): Recommendation[] {
  const recommendations: Recommendation[] = [];
  const { usableArea } = input;

  // Wall insulation recommendation
  if (input.wallInsulation === 'none' || input.wallInsulation === 'partial') {
    recommendations.push({
      id: 'rec-wall-insulation',
      title: 'Docieplenie ścian zewnętrznych',
      description: input.wallInsulation === 'none'
        ? 'Dodanie izolacji termicznej (15-20 cm styropianu lub wełny mineralnej) znacząco obniży straty ciepła.'
        : 'Uzupełnienie lub wymiana istniejącej izolacji na nowoczesną (15+ cm).',
      estimatedCost: {
        min: Math.round(usableArea * 150),
        max: Math.round(usableArea * 300),
      },
      estimatedSavings: Math.round(usableArea * 25),
      epReduction: input.wallInsulation === 'none' ? 40 : 20,
      paybackYears: { min: 6, max: 12 },
      priority: input.wallInsulation === 'none' ? 'high' : 'medium',
    });
  }

  // Roof insulation recommendation
  if (input.roofInsulation === 'none' || input.roofInsulation === 'partial') {
    recommendations.push({
      id: 'rec-roof-insulation',
      title: 'Docieplenie dachu/stropodachu',
      description: 'Izolacja dachu (25-30 cm wełny mineralnej) zmniejszy straty ciepła przez dach.',
      estimatedCost: {
        min: Math.round(usableArea * 80),
        max: Math.round(usableArea * 180),
      },
      estimatedSavings: Math.round(usableArea * 15),
      epReduction: input.roofInsulation === 'none' ? 25 : 12,
      paybackYears: { min: 5, max: 10 },
      priority: input.roofInsulation === 'none' ? 'high' : 'medium',
    });
  }

  // Window replacement recommendation
  if (input.windowType === 'single' || input.windowType === 'double_old') {
    recommendations.push({
      id: 'rec-window-replacement',
      title: 'Wymiana okien',
      description: input.windowType === 'single'
        ? 'Wymiana okien jednoszybowych na trzyszybowe (U<0.9) drastycznie zmniejszy straty ciepła.'
        : 'Wymiana starych okien dwuszybowych na nowoczesne trzyszybowe.',
      estimatedCost: {
        min: Math.round(usableArea * 200),
        max: Math.round(usableArea * 400),
      },
      estimatedSavings: Math.round(usableArea * 18),
      epReduction: input.windowType === 'single' ? 30 : 15,
      paybackYears: { min: 8, max: 15 },
      priority: input.windowType === 'single' ? 'high' : 'medium',
    });
  }

  // Heating system upgrade recommendation
  const inefficientHeating = ['coal', 'stove', 'gas_traditional', 'electric_radiators'];
  if (inefficientHeating.includes(input.heatingSystem) || input.systemAge === 'old') {
    recommendations.push({
      id: 'rec-heating-upgrade',
      title: 'Wymiana źródła ciepła',
      description: getHeatingUpgradeDescription(input.heatingSystem),
      estimatedCost: {
        min: 25000,
        max: 60000,
      },
      estimatedSavings: Math.round(usableArea * 40),
      epReduction: input.heatingSystem === 'coal' ? 80 : 40,
      paybackYears: { min: 5, max: 12 },
      priority: input.heatingSystem === 'coal' || input.systemAge === 'old' ? 'high' : 'medium',
    });
  }

  // PV recommendation
  if (!input.hasPV) {
    const recommendedPV = Math.ceil(usableArea / 15); // ~1 kWp per 15 m²
    recommendations.push({
      id: 'rec-pv-installation',
      title: 'Instalacja fotowoltaiczna',
      description: `Montaż instalacji PV o mocy ${recommendedPV}-${recommendedPV + 3} kWp obniży rachunki za energię i wskaźnik EP.`,
      estimatedCost: {
        min: recommendedPV * 4000,
        max: recommendedPV * 6000,
      },
      estimatedSavings: recommendedPV * 950,
      epReduction: Math.min(epTotal * 0.3, recommendedPV * 15),
      paybackYears: { min: 6, max: 10 },
      priority: 'medium',
    });
  }

  // Recuperation recommendation
  if (!input.renewables.includes('recuperation')) {
    recommendations.push({
      id: 'rec-recuperation',
      title: 'Instalacja rekuperacji',
      description: 'Wentylacja mechaniczna z odzyskiem ciepła zmniejszy straty wentylacyjne o 80-90%.',
      estimatedCost: {
        min: 15000,
        max: 35000,
      },
      estimatedSavings: Math.round(usableArea * 12),
      epReduction: 15,
      paybackYears: { min: 8, max: 15 },
      priority: 'low',
    });
  }

  // Sort by priority (high first) and EP reduction
  return recommendations.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }
    return b.epReduction - a.epReduction;
  });
}

/**
 * Get heating upgrade description based on current system
 */
function getHeatingUpgradeDescription(currentSystem: SimulatorInput['heatingSystem']): string {
  switch (currentSystem) {
    case 'coal':
    case 'stove':
      return 'Wymiana kotła węglowego/pieca na pompę ciepła lub kocioł gazowy kondensacyjny. Obniży koszty i emisję CO2.';
    case 'gas_traditional':
      return 'Wymiana kotła gazowego na nowoczesny model kondensacyjny zwiększy sprawność o 15-20%.';
    case 'electric_radiators':
      return 'Wymiana grzejników elektrycznych na pompę ciepła powietrzną obniży rachunki o 60-70%.';
    default:
      return 'Modernizacja systemu grzewczego poprawi efektywność energetyczną budynku.';
  }
}

// =============================================================================
// MAIN CALCULATION
// =============================================================================

/**
 * Main EP calculation function
 *
 * Calculates the Primary Energy indicator (EP) and all related metrics
 * based on Polish building regulations.
 *
 * @param input - Simulator input data from the form
 * @returns Complete calculation results including energy class and recommendations
 */
export function calculateEP(input: SimulatorInput): SimulatorResult {
  // 0. Runtime validation
  validateSimulatorInput(input);

  // 1. Calculate building geometry
  const geometry = calculateBuildingGeometry(input);

  // 2. Get U-values for building envelope
  const uValues = getUValues(input);

  // 3. Calculate heat loss coefficient
  const heatLossCoefficient = calculateHeatLossCoefficient(geometry, uValues);

  // 4. Adjust for recuperation
  let adjustedHeatLoss = { ...heatLossCoefficient };
  if (input.renewables.includes('recuperation')) {
    adjustedHeatLoss.ventilation *= (1 - RECUPERATION_EFFICIENCY);
    adjustedHeatLoss.total = adjustedHeatLoss.transmission + adjustedHeatLoss.ventilation;
  }

  // 5. Calculate useful energy demand [kWh/year]
  // Apply orientation factor for apartments with known orientation
  const orientationFactor = input.buildingType === 'apartment' && input.apartmentOrientation
    ? ORIENTATION_SOLAR_FACTORS[input.apartmentOrientation] ?? 0.7
    : undefined; // undefined = use default average (0.7)
  const usefulEnergy = calculateUsefulEnergy(adjustedHeatLoss, geometry.volume, geometry.windowArea, orientationFactor, input.climateZone);

  // 6. Calculate primary and final energy, accounting for secondary heating
  const secondaryShare = (input.hasSecondaryHeating && input.secondaryHeatingSystem && input.secondaryHeatingShare)
    ? Math.min(50, Math.max(10, input.secondaryHeatingShare)) / 100
    : 0;
  const primaryShare = 1 - secondaryShare;

  // Primary system
  const primaryEfficiency = getSystemEfficiency(input.heatingSystem, input.systemAge);
  const primaryFinalEnergy = (usefulEnergy * primaryShare) / primaryEfficiency;
  const primaryWi = getPrimaryEnergyCoefficient(input.heatingSystem);
  const primaryPE = primaryFinalEnergy * primaryWi;

  // Secondary system (if enabled)
  let secondaryFinalEnergy = 0;
  let secondaryPE = 0;
  if (secondaryShare > 0 && input.secondaryHeatingSystem) {
    const secondaryEfficiency = getSystemEfficiency(input.secondaryHeatingSystem, 'medium');
    secondaryFinalEnergy = (usefulEnergy * secondaryShare) / secondaryEfficiency;
    const secondaryWi = getPrimaryEnergyCoefficient(input.secondaryHeatingSystem);
    secondaryPE = secondaryFinalEnergy * secondaryWi;
  }

  const finalEnergy = primaryFinalEnergy + secondaryFinalEnergy;
  const primaryEnergy = primaryPE + secondaryPE;

  // 7. Calculate EP for heating [kWh/m²·year]
  const epHeating = primaryEnergy / input.usableArea;

  // 11. Calculate EP for hot water [kWh/m²·year] + useful energy for heating cost comparison
  const hotWaterResult = calculateHotWaterEP(input);
  const epHotWater = hotWaterResult.ep;

  // 12. EP before OZE reduction
  const epBeforeOZE = epHeating + epHotWater;

  // 13. Calculate OZE reduction
  const ozeReduction = calculateOZEReduction(input, epBeforeOZE);

  // 14. Calculate total EP (ensure non-negative unless truly plus-energy)
  const epTotal = epBeforeOZE - ozeReduction;

  // 15. Determine energy class
  const energyClass = determineEnergyClass(epTotal);

  // 16. Get EP limit for building type
  const limitEP = getEPLimit(input.buildingType);

  // 17. Generate recommendations
  const recommendations = generateRecommendations(input, Math.max(0, epTotal));

  return {
    epTotal,
    epHeating,
    epHotWater,
    epCooling: 0, // Cooling not implemented in this version
    energyClass,
    limitEP,
    exceedsLimit: epTotal > limitEP,
    usefulEnergy,
    finalEnergy,
    primaryEnergy,
    hotWaterUsefulEnergy: hotWaterResult.usefulEnergy,
    recommendations,
  };
}
